/**
 * CameraCaptureModal.tsx — HTML5 camera capture for patient photos.
 */
import { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (base64Png: string) => Promise<void>;
}

export function CameraCaptureModal({ open, onClose, onCapture }: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [hasStream, setHasStream] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setHasStream(false);
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = mediaStream;
      setHasStream(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch {
      setError('Unable to access camera. Please check permissions.');
    }
  }, [stopCamera]);

  useEffect(() => {
    if (open) {
      setCapturedImage(null);
      void startCamera();
    }
    return () => { stopCamera(); };
  }, [open, startCamera, stopCamera]);

  function takePhoto() {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) return;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/png');
    setCapturedImage(dataUrl);
    stopCamera();
  }

  function redoPhoto() {
    setCapturedImage(null);
    void startCamera();
  }

  async function acceptPhoto() {
    if (!capturedImage) return;
    setSaving(true);
    try {
      await onCapture(capturedImage);
      onClose();
    } catch {
      setError('Failed to save photo.');
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={saving ? () => {} : onClose} title="Camera Capture" size="lg">
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      <div className="flex flex-col items-center space-y-4">
        {!capturedImage ? (
          <>
            <div className="w-full bg-black rounded-lg overflow-hidden" style={{ maxHeight: '60vh' }}>
              <video ref={videoRef} autoPlay playsInline muted className="w-full" />
            </div>
            <Button onClick={takePhoto} disabled={!hasStream}>Take Photo</Button>
          </>
        ) : (
          <>
            <img src={capturedImage} alt="Captured" className="max-w-full rounded-lg shadow" style={{ maxHeight: '60vh' }} />
            <div className="flex gap-3">
              <Button variant="secondary" onClick={redoPhoto} disabled={saving}>Redo</Button>
              <Button onClick={() => void acceptPhoto()} loading={saving}>Accept</Button>
            </div>
          </>
        )}
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </Modal>
  );
}
